import api from '@/lib/api';

export interface Snippet {
  id: string;
  name: string;
  shortcut: string;
  content: string;
  description?: string;
  category: string;
  tags: string[];
  isGlobal: boolean;
  usageCount: number;
  variables: Record<string, string>;
  attachmentIds: string[];
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  lastUsedAt?: string;
}

export interface CreateSnippetData {
  name: string;
  shortcut: string;
  content: string;
  description?: string;
  category?: string;
  tags?: string[];
  isGlobal?: boolean;
  variables?: Record<string, string>;
  attachmentIds?: string[];
}

export interface UpdateSnippetData {
  name?: string;
  shortcut?: string;
  content?: string;
  description?: string;
  category?: string;
  tags?: string[];
  isGlobal?: boolean;
  variables?: Record<string, string>;
  attachmentIds?: string[];
}

export interface SnippetsResponse {
  success: boolean;
  data: {
    snippets: Snippet[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

export interface SnippetResponse {
  success: boolean;
  data: {
    snippet: Snippet;
  };
}

export interface UseSnippetResponse {
  success: boolean;
  data: {
    processedContent: string;
    snippet: {
      id: string;
      name: string;
      shortcut: string;
      originalContent: string;
      processedContent: string;
      variables: Record<string, string>;
      attachmentIds: string[];
    };
  };
}

class SnippetsService {
  async getSnippets(params: {
    category?: string;
    is_global?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<SnippetsResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.category) queryParams.set('category', params.category);
    if (params.is_global !== undefined) queryParams.set('is_global', params.is_global.toString());
    if (params.search) queryParams.set('search', params.search);
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());

    const response = await api.get<SnippetsResponse>(`/snippets?${queryParams.toString()}`);
    return response.data;
  }

  async getSnippetById(id: string): Promise<SnippetResponse> {
    const response = await api.get<SnippetResponse>(`/snippets/${id}`);
    return response.data;
  }

  async createSnippet(data: CreateSnippetData): Promise<SnippetResponse> {
    const response = await api.post<SnippetResponse>('/snippets', data);
    return response.data;
  }

  async updateSnippet(id: string, data: UpdateSnippetData): Promise<SnippetResponse> {
    const response = await api.put<SnippetResponse>(`/snippets/${id}`, data);
    return response.data;
  }

  async deleteSnippet(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/snippets/${id}`);
    return response.data;
  }

  async useSnippet(id: string, variables: Record<string, string> = {}): Promise<UseSnippetResponse> {
    const response = await api.post<UseSnippetResponse>(`/snippets/${id}/use`, { variables });
    return response.data;
  }

  // Helper method to get snippets by category
  async getSnippetsByCategory(category: string): Promise<Snippet[]> {
    const response = await this.getSnippets({ category, limit: 100 });
    return response.data.snippets;
  }

  // Helper method to search snippets
  async searchSnippets(query: string): Promise<Snippet[]> {
    const response = await this.getSnippets({ search: query, limit: 50 });
    return response.data.snippets;
  }

  // Helper method to get shortcuts for autocomplete
  async getShortcuts(): Promise<string[]> {
    const response = await this.getSnippets({ limit: 100 });
    return response.data.snippets.map(snippet => snippet.shortcut);
  }
}

export default new SnippetsService();